import { useEffect, useState } from 'react';
import axios from 'axios';
import { Trophy, Users, Target, Award, Lightbulb } from 'lucide-react';
import Navigation from '../components/Navigation';

import { API_BASE_URL } from '@/config/api';
const API = API_BASE_URL;

export default function About() {
  const [user, setUser] = useState(null);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(response.data);
      } catch (error) {
        console.error('Error fetching user:', error);
      }
    };
    fetchUser();
  }, []);

  return (
    <>
      <Navigation user={user} />
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-amber-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-white rounded-xl shadow-lg p-8">
            {/* How It Works Section */}
            <div className="mb-8">
              <h2 className="font-bebas text-4xl text-amber-600 mb-6 tracking-wider">
                How It Works
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
                <div className="bg-emerald-50 p-6 rounded-md border-2 border-emerald-200">
                  <div className="bg-amber-600 w-14 h-14 rounded-full flex items-center justify-center mb-4">
                    <Target className="text-white" size={28} />
                  </div>
                  <h3 className="font-bebas text-2xl text-emerald-900 mb-2 tracking-wide">Pick Teams</h3>
                  <p className="font-manrope text-gray-700">
                    Select ONE team from each seed (1-16). That's 16 teams total.
                  </p>
                </div>
                <div className="bg-emerald-50 p-6 rounded-md border-2 border-emerald-200">
                  <div className="bg-amber-600 w-14 h-14 rounded-full flex items-center justify-center mb-4">
                    <Trophy className="text-white" size={28} />
                  </div>
                  <h3 className="font-bebas text-2xl text-emerald-900 mb-2 tracking-wide">Score Points</h3>
                  <p className="font-manrope text-gray-700">
                    Higher seeds = more points. 16 seed win = 25 pts, down to 1 seed = 10 pts.
                  </p>
                </div>
                <div className="bg-emerald-50 p-6 rounded-md border-2 border-emerald-200">
                  <div className="bg-amber-600 w-14 h-14 rounded-full flex items-center justify-center mb-4">
                    <Users className="text-white" size={28} />
                  </div>
                  <h3 className="font-bebas text-2xl text-emerald-900 mb-2 tracking-wide">Track Others</h3>
                  <p className="font-manrope text-gray-700">
                    See what teams your friends picked and compare strategies.
                  </p>
                </div>
                <div className="bg-emerald-50 p-6 rounded-md border-2 border-emerald-200">
                  <div className="bg-amber-600 w-14 h-14 rounded-full flex items-center justify-center mb-4">
                    <Award className="text-white" size={28} />
                  </div>
                  <h3 className="font-bebas text-2xl text-emerald-900 mb-2 tracking-wide">Win Glory</h3>
                  <p className="font-manrope text-gray-700">
                    Climb the leaderboard and claim your bragging rights.
                  </p>
                </div>
              </div>
            </div>

            {/* Strategies Section */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-6">
                <Lightbulb className="text-amber-600" size={32} />
                <h2 className="font-bebas text-4xl text-amber-600 tracking-wider">
                  Strategies
                </h2>
              </div>

              <div className="space-y-5">
                <div className="bg-emerald-50 p-5 rounded-md border-l-4 border-amber-500">
                  <h3 className="font-bebas text-2xl text-emerald-900 mb-2 tracking-wide">1. Just Wing It</h3>
                  <p className="font-manrope text-gray-700">
                    Pick your favorite teams, go with your gut, and let the madness do the rest. 
                    Sometimes the best strategy is no strategy. Half the fun is watching chaos unfold.
                  </p>
                </div>

                <div className="bg-emerald-50 p-5 rounded-md border-l-4 border-amber-500">
                  <h3 className="font-bebas text-2xl text-emerald-900 mb-2 tracking-wide">2. Watch for Friendly Fire</h3>
                  <p className="font-manrope text-gray-700">
                    If you pick two teams that could meet early in the bracket, only one can 
                    advance — the other is done. For example, your 1-seed and your 8 or 9-seed 
                    from the same region will collide in Round 2. Before locking in, check the bracket 
                    to see where your picks might cross paths. Spreading picks across regions gives 
                    each one more room to run.
                  </p>
                </div>

                <div className="bg-emerald-50 p-5 rounded-md border-l-4 border-amber-500">
                  <h3 className="font-bebas text-2xl text-emerald-900 mb-2 tracking-wide">3. The 8/9 Hedge</h3>
                  <p className="font-manrope text-gray-700">
                    Pick the 8-seed and 9-seed from the same region. Since they play each other in 
                    Round 1, you're <strong>guaranteed</strong> one win (17 or 18 points) — but you're 
                    also <strong>guaranteed</strong> one loss. It's safe floor points, but you're capping 
                    your upside since neither team is likely to go on a deep run. A good play if you want 
                    consistency over ceiling.
                  </p>
                </div>

                <div className="bg-emerald-50 p-5 rounded-md border-l-4 border-amber-500">
                  <h3 className="font-bebas text-2xl text-emerald-900 mb-2 tracking-wide">4. Fade the Hype</h3>
                  <p className="font-manrope text-gray-700">
                    That trendy upset pick everyone's talking about? If the whole pool is on it, the 
                    upside shrinks. When everyone zigs, zag. Find value where others aren't looking — 
                    the less popular pick at the same seed could pay off just as much without the crowd 
                    diluting your edge.
                  </p>
                </div>

                <div className="bg-emerald-50 p-5 rounded-md border-l-4 border-amber-500">
                  <h3 className="font-bebas text-2xl text-emerald-900 mb-2 tracking-wide">5. Back the Cinderella</h3>
                  <p className="font-manrope text-gray-700">
                    For seeds 12-16, you're picking a long shot no matter what — so find the one most 
                    likely to pull an upset. A 12-seed over a 5 happens almost every year. A single 
                    14-seed win is worth 23 points. Do your homework on which underdogs have the best 
                    shot, and let the math work in your favor.
                  </p>
                </div>

                <div className="bg-emerald-50 p-5 rounded-md border-l-4 border-amber-500">
                  <h3 className="font-bebas text-2xl text-emerald-900 mb-2 tracking-wide">6. Study the Matchups</h3>
                  <p className="font-manrope text-gray-700">
                    Your 1-seed pick only earns 10 points per win, but a 1-seed with an easy path 
                    could win 4+ games — that's 40+ points from one pick. For each seed, look at which 
                    region gives that team the best chance to go deep. The team with the friendliest 
                    draw is often the smartest choice.
                  </p>
                </div>

                <div className="bg-emerald-50 p-5 rounded-md border-l-4 border-amber-500">
                  <h3 className="font-bebas text-2xl text-emerald-900 mb-2 tracking-wide">7. Check the Injury Report</h3>
                  <p className="font-manrope text-gray-700">
                    A low seed missing a key player is vulnerable — and the team facing them just 
                    became a better pick. Key injuries can turn a tough first-round matchup into a 
                    free win. Check the news before you lock in, especially for those high-value 
                    4 vs 13 and 5 vs 12 games.
                  </p>
                </div>

                <div className="bg-emerald-50 p-5 rounded-md border-l-4 border-amber-500">
                  <h3 className="font-bebas text-2xl text-emerald-900 mb-2 tracking-wide">8. Ride the Hot Hand</h3>
                  <p className="font-manrope text-gray-700">
                    A higher seed coming into the tournament on a winning streak is dangerous. Teams 
                    that won their conference tournament or rattled off 5-6 straight wins have 
                    momentum, confidence, and chemistry — exactly what you need in a single-elimination 
                    format. A hot 11 or 12-seed can be worth more than a flat 4 or 5.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
